import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import AppShell from '../components/AppShell';
import EmptyState from '../components/EmptyState';
import { reading } from '../domain/reading';
import MonthView from '../components/calendar/MonthView';
import WeekView from '../components/calendar/WeekView';
import WeekListView from '../components/calendar/WeekListView';
import DayView from '../components/calendar/DayView';
import CalendarToolbar from '../components/calendar/CalendarToolbar';
import EventPopover from '../components/calendar/EventPopover';
import EventDrawer from '../components/calendar/EventDrawer';
import RecurringScopeDialog, { SCOPES } from '../components/calendar/RecurringScopeDialog';
import { EVENT_MODES } from '../domain/eventModes';
import { editingFromOccurrence, reschedulePatch } from '../domain/eventSeries';
import AttendanceDrawer from '../components/calendar/AttendanceDrawer';
import CategoriesDrawer from '../components/calendar/CategoriesDrawer';
import NoteTemplatesDrawer from '../components/calendar/notes/NoteTemplatesDrawer';
import useCalendarView from '../hooks/useCalendarView';
import useCalendarOccurrences from '../hooks/useCalendarOccurrences';
import { useEvents } from '../data/useEvents';
import { useStudents } from '../data/useStudents';
import { useCategories } from '../data/useCategories';
import { useAttendance } from '../data/useAttendance';
import { useFamilies } from '../data/useFamilies';
import { useLedger } from '../data/useLedger';
import { usePackages } from '../data/usePackages';
import { useMakeup } from '../data/useMakeup';
import { useNotifications } from '../data/useNotifications';
import { useSettings } from '../data/useSettings';
import { useLocale, useMoneyFormat } from '../context/LocaleContext';
import { cancellationRequests, occurrenceNoticeGroups } from '../domain/notifications';
import {
  markedOn, markedOccurrences, removalFootprint, removalSentences, retractionTargets,
  hasRetraction, planEventDelete,
} from '../domain/lessonRemoval';
import { screenZoneLabel } from '../domain/timeZone';
import { displayTimeZone } from '../lib/intlOptions';
import s from './CalendarPage.module.css';

// The calendar. Assembly: which slice is on screen is useCalendarView, what is
// in it is useCalendarOccurrences, the controls are CalendarToolbar, and each
// view renders itself.

const iso = (d) => format(d, 'yyyy-MM-dd');

export default function CalendarPage() {
  const {
    occurrencesBetween, loading, failed, priceFor, removeOccurrence, overrideOccurrence,
    updateSeries, deleteSeries, dropOverridesFrom, series,
  } = useEvents();
  const { getStudent, students } = useStudents();
  const { byId: familyById, contactsFor } = useFamilies();
  const { queue } = useNotifications();
  // `records` and the retraction writer are both here for the delete dialog:
  // the records say which lessons were marked, and so — through their derived
  // ids — name every document a delete would strand. See domain/lessonRemoval.
  const { statusFor, records, voidAttendanceEffects } = useAttendance();
  // Read here and folded in the domain rather than asked of each provider,
  // because what the dialog states and what the confirm writes must be the same
  // documents. Three collections, because attendance writes into three: money,
  // prepaid lessons and make-up credits.
  const { entries: ledgerEntries } = useLedger();
  const { entries: packageEntries } = usePackages();
  const { entries: makeupEntries } = useMakeup();
  const formatMoney = useMoneyFormat();
  // `colorFor` is how every view finds an event's colour (#109, #130), passed
  // down as `studentsById` is, so no view subscribes to a provider to draw a
  // chip. It returns null for an id that names no category, so an
  // uncategorised event renders as one rather than borrowing Lesson's blue.
  const { locations, colorFor: categoryColorFor } = useCategories();
  const { settings } = useSettings();
  const { locale } = useLocale();
  // Where the studio is now. Not the interpreter of any lesson — that is the
  // series' own zone — only what a series' zone is compared against, to decide
  // whether naming it would tell the teacher anything.
  const studioTimeZone = displayTimeZone(settings);

  const nav = useCalendarView();
  const [search, setSearch] = useState('');
  const { occurrences, lessons, blocks } = useCalendarOccurrences({
    occurrencesBetween,
    rangeStart: nav.rangeStart,
    rangeEnd: nav.rangeEnd,
    statusFor,
    getStudent,
    search,
  });

  // What a new event should start from: a click gives a date and a time, a drag
  // also gives how long it was. Null means "no particular slot".
  const [addFrom, setAddFrom] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  // Which of the three "+ Add Event" forms to open. Clicking a slot on the grid
  // opens the full one, as MMS does.
  const [addMode, setAddMode] = useState(EVENT_MODES.full);
  const [editing, setEditing] = useState(null);
  const [popover, setPopover] = useState(null);
  const [attendanceTarget, setAttendanceTarget] = useState(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [noteTemplatesOpen, setNoteTemplatesOpen] = useState(false);
  // The occurrence a delete is pending on, while the scope question is open.
  const [deleting, setDeleting] = useState(null);

  const openAdd = (from = null, mode = EVENT_MODES.full) => {
    setAddFrom(from);
    setAddMode(mode);
    setAddOpen(true);
  };
  const openAddAt = (date, durationMin) => openAdd({
    date: iso(date),
    time: format(date, 'HH:mm'),
    durationMin: durationMin || null,
  });

  // What EventDrawer needs to edit an occurrence: the occurrence's own values,
  // plus the recurrence settings, which live on the rule. The projection is in
  // the domain, which explains the three dates involved.
  const editingShape = (occurrence) =>
    editingFromOccurrence(occurrence, series.find((x) => x.id === occurrence.seriesId) || {});

  // "EDT" beside a lesson time, only for a lesson whose series was created in a
  // zone the studio is no longer in (screenZoneLabel in domain/timeZone.js).
  //
  // Resolved here because the zone is a property of the series and the popover
  // and the dialog are handed an occurrence. The fold does not copy it onto the
  // occurrence: that shape is pinned by golden vectors against two ports
  // (domain/schedule.js).
  const zoneLabelFor = (occurrence) => (occurrence ? screenZoneLabel(
    series.find((x) => x.id === occurrence.seriesId)?.timeZone || null,
    studioTimeZone,
    occurrence.date,
    occurrence.startTime,
    locale,
  ) : '');

  // Resizing one lesson writes an occurrence override, not a series edit: the
  // teacher dragged one Tuesday longer. The bare override MMS's drag makes — the
  // length alone, no scope question, no conflict check — and the one the
  // phones' Reschedule writes, from the same rule (`reschedulePatch`, #482).
  const resizeLesson = (lesson, durationMin) => {
    const occurrence = lesson._occurrence || lesson;
    if (!occurrence?.id) return;
    const patch = reschedulePatch(editingShape(occurrence), { duration: durationMin });
    if (Object.keys(patch).length) overrideOccurrence(occurrence.id, patch);
  };

  // ── Moving one lesson by dragging it (#571) ───────────────────────────────
  //
  // The same write as the resize above and the phones' Reschedule: the core's
  // `reschedule_patch`, one bare override on that occurrence. A series entry
  // moves alone, with no scope question — #482's rule — and, as on the resize
  // and on the phones, no conflict check. Where it lands is the domain's
  // (domain/calendarDrop.js); this is the write.
  const rescheduleOne = (occurrence, to) => {
    if (!occurrence?.id) return;
    const patch = reschedulePatch(editingShape(occurrence), to);
    if (Object.keys(patch).length) overrideOccurrence(occurrence.id, patch);
  };

  // The time grid — Week · Agenda and the Day view: the day and the
  // five-minute step it was let go on.
  const moveLesson = (lesson, { date, time }) => {
    rescheduleOne(lesson._occurrence || lesson, { date, time });
  };

  // The month grid: the day only, at the entry's own time, and then the edit
  // window open on it so the time can be changed, opening on the existing
  // time. The window is handed the occurrence as it now stands, so it opens on
  // the new day before the override comes back through the read store. Closing
  // it leaves the move made.
  const moveEventToDay = (occurrence, date) => {
    rescheduleOne(occurrence, { date });
    setEditing({ ...occurrence, date });
  };

  // The families a delete would notify, and which of their students are on the
  // lesson. Used twice: the dialog resolves addresses from it to warn who cannot
  // be reached, and the confirm queues against the same list, so the warning
  // and the send describe the same people.
  //
  // A family's group holds only its own students, and the server-side facts
  // loader guards the same way.
  //
  // A group carries the family's contacts, because `cancellationRecipients`
  // resolves both the addresses and the "no address on file" warning from them.
  // Every contact, not the billing one (#319, #413): MMS's delete dialog
  // captions its tick `Email parents`, plural, and its composer BCCs every
  // contact on the family.
  //
  // The grouping is `occurrenceNoticeGroups` in domain/notifications.js.
  const noticeGroups = (occurrence) => {
    if (!occurrence) return [];
    return occurrenceNoticeGroups(
      (occurrence.studentIds || []).map((sid) => getStudent(sid)),
      { familyById, contactsFor },
    );
  };

  // Telling the families that their lessons are off.
  //
  // Never one message per lesson: "this and following" on an indefinite series
  // is an unbounded number of lessons. The scope and the date go in the context
  // so the sender can say which lessons, as the invoice function renders from
  // the domain rather than from text the client composed.
  //
  // One message per family, to every reachable contact on it. MMS addresses
  // students individually here too; a student is not a message destination
  // here (#792). The addressing and the id keying are in
  // domain/notifications.js.
  const queueCancellationNotices = async (occurrence, scope, chosen) => {
    await queue(cancellationRequests({
      occurrence, scope, chosen, groups: noticeGroups(occurrence),
    }));
  };

  // Held steady while the dialog is open: it re-resolves addresses from this on
  // every keystroke in the cancellation message. `noticeGroups` reads only
  // these four, which is why they are the whole dependency list.
  const deletingGroups = useMemo(
    () => noticeGroups(deleting),
    [deleting, students, familyById, contactsFor],
  );

  // What the delete would do, for each scope — `planEventDelete` in
  // domain/lessonRemoval.js. The reasons for each branch are here, beside the
  // writes below.
  const deletingRule = deleting ? series.find((x) => x.id === deleting.seriesId) : null;
  const deletePlan = (scope) => planEventDelete({
    occurrence: deleting, savedRule: deletingRule || null, scope,
  });
  const deletingPlan = deletePlan(SCOPES.following);

  // A one-time event has one entry, so the delete dialog has no scope to ask
  // about — only who to tell.
  const deletingIsRecurring = deletingPlan.recurring;

  // ── What the delete would leave behind (#157) ─────────────────────────────
  //
  // Taking attendance writes a charge, a make-up credit, a prepaid consume and
  // any payment, all keyed off the occurrence, so a delete reverses them —
  // always, rather than refusing or cascading (the top of
  // domain/lessonRemoval.js). Both scopes are worked out while the dialog is
  // open, so the figure moves with the radio.
  //
  // The cut is the rule date, the one the delete itself uses below: a moved
  // lesson is still produced by its rule date. When "this and following" from a
  // series' own first occurrence deletes the whole series, the footprint is the
  // whole series too: a series shifted later can hold attendance on dates
  // before its own start, which a `from` cut would miss.
  const deletingCut = deletingPlan.cut;
  const followingIsWholeSeries = deletingPlan.followingIsWholeSeries;

  const deletingFootprints = useMemo(() => {
    if (!deleting) return { [SCOPES.one]: null, [SCOPES.following]: null };
    const money = {
      ledgerEntries: ledgerEntries,
      packageEntries: packageEntries,
      makeupEntries: makeupEntries,
    };
    const forOne = removalFootprint({ records: markedOn(records, deleting.id), ...money });
    // A one-time event is deleted outright, and its series holds the one
    // occurrence, so the two are the same set. Computing it as the "one" scope
    // keeps the dialog's default radio on the right figure with or without the
    // radios on screen.
    const forFollowing = deletingIsRecurring
      ? removalFootprint({
        records: markedOccurrences(records, {
          seriesId: deleting.seriesId,
          fromDate: deletingPlan.followingFrom,
        }),
        ...money,
      })
      : forOne;
    return { [SCOPES.one]: forOne, [SCOPES.following]: forFollowing };
  }, [
    deleting, deletingCut, deletingIsRecurring, followingIsWholeSeries,
    records, ledgerEntries, packageEntries, makeupEntries,
  ]);

  const deletingMoney = useMemo(() => ({
    [SCOPES.one]: removalSentences(deletingFootprints[SCOPES.one], formatMoney),
    [SCOPES.following]: removalSentences(deletingFootprints[SCOPES.following], formatMoney),
  }), [deletingFootprints, formatMoney]);

  const isEmptyMonth = (!loading || failed) && occurrences.length === 0 && nav.view === 'month';
  // Week · Agenda and Day are a time grid over the whole day (#749), which
  // scrolls inside itself under its day headings: the page is held to the
  // window's height for them, and the grid takes what the toolbar leaves.
  const timeGrid = !isEmptyMonth && (nav.view === 'day' || (nav.view === 'week' && nav.weekMode !== 'list'));

  return (
    <AppShell title="Calendar">
      <div className={timeGrid ? s.calFill : s.cal}>
        <CalendarToolbar
          title={nav.title}
          view={nav.view}
          weekMode={nav.weekMode}
          search={search}
          onStep={nav.step}
          onToday={nav.today}
          onViewChange={nav.setView}
          onWeekModeChange={nav.setWeekMode}
          onSearchChange={setSearch}
          onAddEvent={(mode) => openAdd(null, mode)}
          onCategories={() => setCategoriesOpen(true)}
          onNoteTemplates={() => setNoteTemplatesOpen(true)}
        />

        {isEmptyMonth ? (
          <div className={s.emptyWrap}>
            {search.trim() ? (
              <EmptyState
                reading={reading(!loading, occurrences.length, failed)}
                title={`No events match “${search.trim()}”`}
                description="Try a student's name or a category. Clearing the search brings the month back."
                actionLabel="Clear search"
                onAction={() => setSearch('')}
              />
            ) : (
              <EmptyState
                reading={reading(!loading, occurrences.length, failed)}
                title="Nothing scheduled this month"
                description="Add a lesson to get your teaching week on the calendar."
                actionLabel="Add Event"
                // Quick-Add Lesson, on the month in view (#573) — the same form
                // as the Day view's "+ Add a lesson" and the toolbar's
                // Quick-Add.
                onAction={() => openAdd({ date: iso(nav.cursor), time: null, durationMin: null }, EVENT_MODES.quick)}
              />
            )}
          </div>
        ) : nav.view === 'month' ? (
          <MonthView
            month={nav.cursor}
            occurrences={occurrences}
            studentsById={getStudent}
            categoryColorFor={categoryColorFor}
            onEventClick={(o, el) => setPopover({ occurrence: o, anchor: el })}
            onDayClick={(d) => openAdd({ date: d, time: null, durationMin: null })}
            onMoveEvent={moveEventToDay}
          />
        ) : nav.view === 'week' && nav.weekMode === 'list' ? (
          <WeekListView
            weekStart={nav.weekStart}
            occurrences={occurrences}
            studentsById={getStudent}
            categoryColorFor={categoryColorFor}
            onEventClick={(o, el) => setPopover({ occurrence: o, anchor: el })}
          />
        ) : nav.view === 'week' ? (
          <WeekView
            weekStart={nav.weekStart}
            blocks={blocks}
            categoryColorFor={categoryColorFor}
            onLessonClick={(l, el) => setPopover({ occurrence: l._occurrence, anchor: el })}
            onSlotClick={openAddAt}
            onResizeLesson={resizeLesson}
            onMoveLesson={moveLesson}
          />
        ) : (
          <DayView
            date={nav.cursor}
            lessons={lessons.filter((l) => iso(l.date) === iso(nav.cursor))}
            blocks={blocks.filter((l) => iso(l.date) === iso(nav.cursor))}
            categoryColorFor={categoryColorFor}
            onLessonClick={(l, el) => setPopover({ occurrence: l._occurrence, anchor: el })}
            // Quick-Add Lesson on the day in view (#573): see DayView.jsx.
            onAddLesson={() => openAdd({ date: iso(nav.cursor), time: null, durationMin: null }, EVENT_MODES.quick)}
            onSlotClick={openAddAt}
            onResizeLesson={resizeLesson}
            onMoveLesson={moveLesson}
            loading={loading}
            failed={failed}
          />
        )}
      </div>

      {popover ? (
        <EventPopover
          occurrence={popover.occurrence}
          anchor={popover.anchor}
          students={students}
          locations={locations}
          priceFor={priceFor}
          statusFor={statusFor}
          zoneLabel={zoneLabelFor(popover.occurrence)}
          onClose={() => setPopover(null)}
          onEdit={() => { setEditing(popover.occurrence); setPopover(null); }}
          onDelete={() => {
            // Always the dialog, even for a one-time event: deleting one lesson
            // of a repeating series is the same ambiguity as editing one, and a
            // one-time event still has a family to tell. MMS asks in both cases
            // and drops the scope radios where there is nothing to choose.
            setDeleting(popover.occurrence);
            setPopover(null);
          }}
          onTakeAttendance={() => {
            const studentId = popover.occurrence.studentIds[0];
            if (!studentId) return;
            setAttendanceTarget({ occurrence: popover.occurrence, studentId });
            setPopover(null);
          }}
        />
      ) : null}

      <CategoriesDrawer open={categoriesOpen} onClose={() => setCategoriesOpen(false)} />

      <NoteTemplatesDrawer
        open={noteTemplatesOpen}
        onClose={() => setNoteTemplatesOpen(false)}
      />

      {/* Both drawers are mounted only while open: EventDrawer builds its form
          in a lazy initialiser, which runs once per mount, so a kept-mounted
          drawer freezes at whatever the props were on first render. */}
      {addOpen ? (
        <EventDrawer
          open
          mode={addMode}
          onClose={() => { setAddOpen(false); setAddFrom(null); }}
          initialDate={addFrom?.date || iso(nav.cursor)}
          initialTime={addFrom?.time}
          initialDuration={addFrom?.durationMin}
        />
      ) : null}

      {editing ? (
        <EventDrawer open onClose={() => setEditing(null)} editing={editingShape(editing)} />
      ) : null}

      <RecurringScopeDialog
        open={Boolean(deleting)}
        action="delete"
        scopes={deletingIsRecurring}
        // What the dialog names under "This event", and who it warns about.
        // Both are resolved here, from the same data the notices are queued
        // against, rather than looked up inside the dialog.
        detail={deleting ? {
          categoryLabel: deleting.categoryLabel,
          date: deleting.date,
          startTime: deleting.startTime,
          // Named only when it would tell the teacher something: this is the
          // last thing read before a lesson goes, and "4:00 PM" that is
          // somebody else's four o'clock is the wrong thing to confirm.
          zoneLabel: zoneLabelFor(deleting),
        } : null}
        noticeGroups={deletingGroups}
        money={deletingMoney}
        onCancel={() => setDeleting(null)}
        onConfirm={async (scope, chosen) => {
          const target = deleting;
          const plan = deletePlan(scope);
          const footprint = deletingFootprints[scope];
          setDeleting(null);
          if (!target) return;
          await queueCancellationNotices(target, scope, chosen);

          // Before the schedule write. Both are batches and neither depends on
          // the other — the ids are derived from the occurrence and the
          // student. Retract first and fail, and a voided charge sits against a
          // lesson still on the calendar, which the teacher can delete again;
          // delete first and fail, and the charge is orphaned against a lesson
          // that is gone (#157).
          //
          // Unconditional (domain/lessonRemoval.js): `hasRetraction` only skips
          // a no-op batch for an unmarked lesson.
          if (footprint && hasRetraction(footprint)) {
            await voidAttendanceEffects(retractionTargets(footprint));
          }

          if (!plan.recurring) {
            // A one-time event is deleted outright: there is no series for it
            // to remain part of, and MMS removes it from the calendar.
            await deleteSeries(target.seriesId);
            return;
          }
          if (plan.action === 'removeOccurrence') {
            // One entry of a series is removed the only way a rule allows: its
            // date goes into `exceptions` and its override goes (#152).
            await removeOccurrence(target.id);
            return;
          }
          // On the rule's date, not the day it is taught: a moved lesson is
          // still produced by its rule date.
          //
          // The same value as `deletingCut`, from the one plan (#157): the
          // money block above states what the lessons from this cut billed, so
          // the cut the figure was taken at and the cut the schedule is broken
          // at must be one value.
          //
          // "This and following" ends the rule the day before, so the lessons
          // stop existing, with no unbounded loop over a series that may repeat
          // forever. Overrides from this date on go too, unreachable once the
          // rule no longer reaches them.
          await dropOverridesFrom(target.seriesId, plan.dropOverridesFrom);

          if (plan.action === 'deleteSeries') {
            // Deleting from a series' own first occurrence means all of it.
            // Ending it the day before would leave a rule that stops before it
            // starts — a husk every later query expands and discards. Same
            // reasoning as the edit flow's split.
            await deleteSeries(target.seriesId);
            return;
          }
          await updateSeries(target.seriesId, { endDate: plan.endDate });
        }}
      />

      <AttendanceDrawer
        open={Boolean(attendanceTarget)}
        onClose={() => setAttendanceTarget(null)}
        occurrence={attendanceTarget?.occurrence}
        studentId={attendanceTarget?.studentId}
      />
    </AppShell>
  );
}
